import { useQuery } from '@apollo/client';
import { practitionersQuery } from 'Services/BeGateway/Practitioner/__generated__/practitionersQuery';
import { PRACTITIONERS_QUERY } from 'Services/BeGateway/Practitioner/practitioner.services';
import { getDefaultDateRange } from 'Talk/components/PractitionerDrawer/helpers/getDefaultDateRange';
import { BEGatewayQueryContext, defaultQueryConfig } from 'utils/apollo';
import { mergeRelayPageResult } from 'utils/relay';
import { PractitionerWhere } from 'Services/BeGateway/__generated__/beGatewayTypes';
import { useUserLocation } from 'Account/hooks';
import { useToday } from '.';

const INITIAL_PRACTITIONER_LOAD_COUNT = 200; // HACK: Set to this as pagination currently broken.  Revert when fixed!

interface UsePractitionersProps {
  practitionersQueryWhere: PractitionerWhere;
  limit?: number;
  cacheConfig?: 'no-cache' | 'cache-and-network';
}

export const usePractitioners = ({
  practitionersQueryWhere,
  limit,
  cacheConfig,
}: UsePractitionersProps) => {
  const today = useToday();
  const defaultDateRange = getDefaultDateRange(today);
  const {
    hasValidLocationData: userHasValidLocationData,
    userLocationData,
    loading: loadingLocation,
  } = useUserLocation();
  const userLocation = userLocationData?.user;

  const practitionersQueryVariables = {
    first: limit ?? INITIAL_PRACTITIONER_LOAD_COUNT,
    ...defaultDateRange,
    filter: {
      country: userLocation?.country?.value,
      state: userLocation?.state?.value,
      where: practitionersQueryWhere,
    },
  };

  const { data, fetchMore, loading, error, refetch } =
    useQuery<practitionersQuery>(PRACTITIONERS_QUERY, {
      fetchPolicy: cacheConfig ?? 'cache-and-network',
      variables: {
        ...practitionersQueryVariables,
      },
      skip: !userHasValidLocationData,
      ...BEGatewayQueryContext,
      ...defaultQueryConfig,
    });

  const fetchMorePractitioners = () => {
    void fetchMore({
      variables: {
        offset: data?.practitioners?.edges?.length,
        after: data?.practitioners?.pageInfo?.endCursor,
        first: 6,
        ...defaultDateRange,
      },
      ...BEGatewayQueryContext,
      updateQuery: mergeRelayPageResult<practitionersQuery, 'practitioners'>(
        'practitioners',
      ),
    });
  };

  return {
    data,
    loading: loading || loadingLocation,
    error,
    refetch: async () => refetch({ ...practitionersQueryVariables }),
    fetchMorePractitioners,
  };
};
